/* eslint-disable no-restricted-globals */
import Multiselect from 'vue-multiselect';
import clonedeep from 'lodash.clonedeep';
import StringHelperService from 'services/string-helper.service';
import Constants from '../../constants/column-name/column-name.constants';
import AjaxDataService from '../../../services/ajax.service';

export default {
  data() {
    return {
      timerDoneTyping: null
    };
  },
  methods: {
    getSelectedItem() {
      // on first load, get the child data store
      const { formProps, otherMappedProps, childStore, childSearchKey } = this.mapping;
      const itemID = this.formData[formProps.idField];
      const itemCode = this.formData[formProps.codeField];
      if (!this.isLoaded) {
        if (childStore !== undefined && childSearchKey !== '' && itemID > 0) {
          this.isLoaded = true;
          this.loadChildDependentData(childStore, childSearchKey, formProps);
        }
      }
      // eslint-disable-next-line no-restricted-globals
      if ((!isNaN(itemID) && itemID > 0) || (isNaN(itemID) && itemID?.length > 0) || itemCode?.length > 0) {
        return this.mapOtherPropsFromForm(this.getMappedLookupItem(), otherMappedProps);
      }
      return [];
    },
    getMappedLookupItem() {
      const { lookups, formProps } = this.mapping;
      const item = {};

      Object.entries(lookups).forEach(([key, value]) => {
        item[value] = this.formData[formProps[key]];
      });

      return item;
    },
    mapOtherPropsFromForm(item, otherMappedProps) {
      if (otherMappedProps !== undefined) {
        Object.entries(otherMappedProps).forEach(([key, value]) => {
          // eslint-disable-next-line no-param-reassign
          item[value] = this.formData[key];
        });
      }
      return item;
    },
    async getServerSideValues(newValue) {
      const { getSelectedItemServerSide, lookups, serverIdKey, serverIdOperator } = this.mapping;

      if (getSelectedItemServerSide) {
        const { searchEndPoint } = this.globalStore.state.AutoCompletes[this.store];
        let { canUseCancelToken } = this.globalStore.state.AutoCompletes[this.store];

        if (!searchEndPoint) {
          return newValue;
        }

        if (canUseCancelToken !== false) {
          canUseCancelToken = true;
        }

        const getParams = {
          pageSize: 0,
          [serverIdKey]: newValue[lookups.idField],
          [serverIdOperator]: 'eq'
        };

        const serviceData = await AjaxDataService.getData(searchEndPoint, getParams, canUseCancelToken);

        if (serviceData.data.length) {
          return serviceData.data[0];
        }
      }

      return newValue;
    },
    async setSelectedItem(newValue) {
      const setVal = await this.getServerSideValues(newValue);
      // This means that we re-selected the same value, we want to ignore this.
      if (this.isSelecting && setVal === null) {
        return;
      }
      this.setMappedLookupItem(setVal);
      this.mapOtherPropsToForm(setVal);
      this.loadChildDependentData();
    },
    setMappedLookupItem(newValue) {
      const { formProps, lookups } = this.mapping;

      Object.entries(lookups).forEach(([key, value]) => {
        this.formData[formProps[key]] =
          newValue === null ||
          (newValue[value] === this.emptyItem &&
            (newValue[lookups.idField] === null ||
              newValue[lookups.idField] === this.emptyItem ||
              newValue[lookups.idField] === 0))
            ? null
            : newValue[value];
      });
    },
    mapOtherPropsToForm(newValue) {
      const { otherMappedProps } = this.mapping;

      if (otherMappedProps !== undefined) {
        Object.entries(otherMappedProps).forEach(([key, value]) => {
          this.formData[key] = newValue === null ? null : newValue[value];
        });
      }
    },
    loadChildDependentData() {
      const { childStore, childSearchKey, formProps } = this.mapping;
      if (childStore !== undefined && childSearchKey !== '') {
        const payload = {
          storeName: childStore,
          searchKey: childSearchKey,
          searchValue: this.formData[formProps.idField]
        };
        this.globalStore.dispatch('loadDataSource', payload);
      }
    },
    scrollToSelectedOption() {
      if (this.mapping.searchOnOpen) {
        // When a server-side auto-complete is opened we want to search for
        // an initial set of values before the user types anything if they
        // haven't already selected or started a search for an item.
        if (this.dataSource.length <= 0 || this.itemIsNull(this.selectedItem) || this.searchOnOpen) {
          this.searchChange();
        }
      }

      /*
        Calling a function like this allows me to change the scope of "this" to the multiselect Vue instance.
        That way I can get access to it's data and computed properties.
        */
      (function scrollTo(allowEmpty) {
        if (!this.hideSelected && this.value && this.trackBy) {
          // Edge case for comment auto completes.
          const { commentText, commentId } = Constants.Comment;
          const trackBy = this.trackBy === commentText.field ? commentId.field : this.trackBy;

          const optionPosition = this.options?.findIndex(opt => opt[trackBy] === this.value[trackBy]);
          const filteredOptionPosition = this.filteredOptions?.findIndex(opt => opt[trackBy] === this.value[trackBy]);

          /*
            Check if the selected option is already available in the limited dropdown,
            or the options limit is greater than the option list length,
            then select the option and scroll to it.
            */
          if (filteredOptionPosition >= 0 || this.optionsLimit > this.options.length) {
            this.pointer = filteredOptionPosition;

            if (this.$refs.list) {
              this.$nextTick(() => {
                this.$refs.list.scrollTop = this.pointer * this.$el.querySelector('.multiselect__element')?.offsetHeight;
              });
            }
            return;
          }

          // Return if the option doesn't exist.
          if (optionPosition === -1) {
            return;
          }

          /*
            Move selected item to the front of the list.
            If multiselect can be empty,
            then move selected item to the second position.
            */
          const splicedItem = this.options.splice(optionPosition, 1)[0];
          const position = allowEmpty ? 1 : 0;

          this.options.splice(position, 0, splicedItem);
          this.pointer = position;

          /*
            https://github.com/shentao/vue-multiselect/blob/master/src/multiselectMixin.js#L339
            Updates the computed value, `filteredOptions`, by changing `optionsLimit`.
            This is not ideal.
            The value should have updated when splicing `options`.
            Computed options update by changing a reactive dependency which `options` should be a part of.
            */
          this.optionsLimit += 1;
          this.optionsLimit -= 1;
        }

        this.pointerDirty = false;
      }.bind(this.$refs[this.uniqueRef], this.allowEmpty)());
    },
    checkForEmptyOption() {
      let isNoneSelected = this.selectedItem === this.emptyItem;

      if (typeof this.selectedItem === 'object') {
        isNoneSelected = Object.values(this.selectedItem).some(prop => prop === this.emptyItem);
      }

      if (this.allowEmpty && isNoneSelected) {
        this.selectedItem = null;
      }
    },
    selectAddedItem(item) {
      this.selectedItem = item;
    },
    itemIsNull(item) {
      return item === null || item.length <= 0;
    },
    IsNullOrWhiteSpace(text) {
      return text == null || text === '' || text === undefined;
    },
    MoveEmptyOptionToTheTop() {
      // Find the empty item
      const emptyOption = this.dataSource.find(x => x[this.mapping.lookups.nameField] === this.emptyItem);
      this.MoveOptionToTheTop(emptyOption);
    },
    MoveOptionToTheTop(option) {
      if (option) {
        if (this.DataSourceContainsOption(option)) {
          // remove the option
          this.dataSource.splice(this.DataSourceIndexOf(option), 1);
        }
        // add the option to the top of the list
        this.dataSource.unshift(option);
      }
    },
    DataSourceContainsOption(option) {
      return this.DataSourceIndexOf(option) !== -1;
    },
    DataSourceIndexOf(option) {
      return this.dataSource.findIndex(x => x[this.mapping.lookups.idField] === option[this.mapping.lookups.idField]);
    },
    // allows for the user to select custom values (only works with Third-Parties at the moment)
    async searchChange(searchText) {
      const execSearch = async () => {
        if (this.mapping.isServerSide) {
          let payload = {
            storeName: this.store,
            searchKey: this.mapping.serverSearchKey,
            searchValue: searchText
          };

          if (this.store === 'Fluid' || this.store === 'FluidBrand') {
            payload = {
              ...payload,
              FluidTypeIDQuery: this.fluidInfo.fluidTypeID
            };
          }

          if (this.store === 'FluidWeight') {
            payload = {
              ...payload,
              FluidClassQuery: this.fluidInfo.fluidClass
            };
          }
          if (this.store === 'FluidBaselineSample' && Object.keys(this.fluidInfo).length > 0) {
            payload = {
              ...payload,
              FluidTypeNameQuery: this.fluidInfo.fluidType,
              FluidIDQuery: this.fluidInfo.fluidId,
              searchText
            };
          }
          await this.globalStore.dispatch('loadDataSource', payload);

          this.dataSource = this.MoveEmptyOptionToTheTop();

          if (this.allowCreateNew) {
            const trimmedSearchText = searchText?.trim();
            const exactMatch = this.dataSource.find(
              item => item[this.labelField]?.toLowerCase() === trimmedSearchText?.toLowerCase()
            );
            if (!exactMatch && trimmedSearchText !== '') {
              this.dataSource = [
                ...this.dataSource,
                {
                  [this.labelField]: trimmedSearchText,
                  [this.mapping.lookups.idField]: null,
                  isNewOption: true
                }
              ];
            }
          }

          // if the list contains the selected option, move it to the top
          if (!this.itemIsNull(this.selectedItem) && this.DataSourceContainsOption(this.selectedItem)) {
            this.MoveOptionToTheTop(this.selectedItem);
          }
          // if the list doesn't contain the selected option, and nothing is searched for, move
          // the selected item to the top
          if (!this.itemIsNull(this.selectedItem) && this.IsNullOrWhiteSpace(searchText)) {
            this.MoveOptionToTheTop(this.selectedItem);
          }
        }

        // Reset pointer to first item when searching
        (function selectFirstItem() {
          this.pointer = 0;
          this.pointerDirty = false;
        }.bind(this.$refs[this.uniqueRef])());
      };

      window.clearTimeout(this.timerDoneTyping);
      this.timerDoneTyping = window.setTimeout(execSearch, 250);
    },
    addEditClass(field) {
      return `add-edit-${StringHelperService.toKebabCase(field)}`;
    },
    selectMultiselectOption(event) {
      // select option if tab or right arrow is pressed
      if (event.keyCode === 9 || event.keyCode === 39) {
        // eslint-disable-next-line no-unused-expressions
        event.target.parentElement.nextElementSibling
          .querySelector('.multiselect__option--highlight:not(.multiselect__option--selected)')
          ?.click();
      }
    },
    prependBlankOption(dataSource) {
      const data = clonedeep(dataSource);

      if (this.allowEmpty && !this.isServerSide) {
        data.unshift(Object.fromEntries(Object.values(this.mapping.lookups).map(value => [value, this.emptyItem])));
      }

      if (this.allowCreateNew && this.$refs[this.uniqueRef]?.search) {
        const searchText = this.$refs[this.uniqueRef].search.trim();
        const exactMatch = data.find(item => item[this.labelField]?.toLowerCase() === searchText?.toLowerCase());
        if (!exactMatch && searchText !== '') {
          data.push({
            [this.labelField]: searchText,
            [this.mapping.lookups.idField]: null,
            isNewOption: true
          });
        }
      }

      return data;
    }
  },
  props: {
    store: {
      type: String,
      required: true
    },
    placeholder: {
      type: String
    },
    searchOnOpen: {
      type: Boolean,
      default() {
        return false;
      }
    },
    allowEmpty: {
      type: Boolean,
      default() {
        return true;
      }
    },
    mapping: {
      type: Object
    },
    hasDescription: {
      type: Boolean,
      default() {
        return false;
      }
    },
    includeInfoButton: {
      type: Boolean,
      default() {
        return false;
      }
    },
    allowAdditions: {
      type: Boolean,
      default() {
        return false;
      }
    },
    formData: {
      type: Object
    },
    openDirection: {
      type: String,
      default() {
        return '';
      }
    },
    tabindex: {
      type: Number,
      default() {
        return 0;
      }
    },
    isWater: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    removeLabelClass: Boolean,
    clearRightPadding: Boolean,
    removeOptionsLimit: Boolean
  },
  components: {
    Multiselect
  }
};
